import React, { createContext, useContext, useState, useEffect } from 'react';

const TimezoneContext = createContext(null);

export const useTimezone = () => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within a TimezoneProvider');
  }
  return context;
};

export const TimezoneProvider = ({ children }) => {
  const [timezone, setTimezone] = useState(() => {
    // Get from localStorage or detect automatically
    const saved = localStorage.getItem('user_timezone');
    if (saved) return saved;
    
    // Auto-detect using Intl API
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch {
      return 'America/Edmonton'; // Default to Alberta timezone
    }
  });

  const [timezoneAbbr, setTimezoneAbbr] = useState('');
  const [utcOffset, setUtcOffset] = useState(0);

  useEffect(() => {
    // Calculate timezone abbreviation and offset
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(now);
      const tzPart = parts.find(p => p.type === 'timeZoneName');
      setTimezoneAbbr(tzPart?.value || '');

      // Calculate UTC offset in hours
      const localDate = new Date();
      const tzDate = new Date(localDate.toLocaleString('en-US', { timeZone: timezone }));
      const utcDate = new Date(localDate.toLocaleString('en-US', { timeZone: 'UTC' }));
      const offsetMs = tzDate.getTime() - utcDate.getTime();
      setUtcOffset(offsetMs / (1000 * 60 * 60));
    } catch (err) {
      console.error('Failed to calculate timezone info:', err);
    }
  }, [timezone]);

  // Save timezone preference
  const updateTimezone = (newTimezone) => {
    setTimezone(newTimezone);
    localStorage.setItem('user_timezone', newTimezone);
  };

  // Convert UTC date to local timezone
  const toLocalTime = (utcDateString) => {
    if (!utcDateString) return null;
    try {
      const date = new Date(utcDateString);
      return new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    } catch {
      return new Date(utcDateString);
    }
  };

  // Format date in local timezone
  const formatLocalTime = (utcDateString, options = {}) => {
    if (!utcDateString) return '';
    try {
      const date = new Date(utcDateString);
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        ...options
      }).format(date);
    } catch {
      return utcDateString;
    }
  };

  // Format game time with timezone
  const formatGameTime = (utcDateString) => {
    if (!utcDateString) return '';
    try {
      const date = new Date(utcDateString);
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
    } catch {
      return utcDateString;
    }
  };

  // Format full game datetime
  const formatGameDateTime = (utcDateString) => {
    if (!utcDateString) return '';
    try {
      const date = new Date(utcDateString);
      return new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      }).format(date);
    } catch {
      return utcDateString;
    }
  };

  // Get common timezone options
  const timezoneOptions = [
    { value: 'America/St_Johns', label: 'Newfoundland (NST)' },
    { value: 'America/Halifax', label: 'Atlantic (AST)' },
    { value: 'America/Toronto', label: 'Eastern (EST)' },
    { value: 'America/Winnipeg', label: 'Central (CST)' },
    { value: 'America/Edmonton', label: 'Mountain (MST)' },
    { value: 'America/Vancouver', label: 'Pacific (PST)' },
    { value: 'America/New_York', label: 'New York (EST)' },
    { value: 'America/Chicago', label: 'Chicago (CST)' },
    { value: 'America/Denver', label: 'Denver (MST)' },
    { value: 'America/Los_Angeles', label: 'Los Angeles (PST)' },
    { value: 'America/Phoenix', label: 'Arizona (MST)' },
    { value: 'Europe/London', label: 'London (GMT)' },
    { value: 'Europe/Paris', label: 'Paris (CET)' },
    { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
    { value: 'Australia/Sydney', label: 'Sydney (AEDT)' },
  ];

  return (
    <TimezoneContext.Provider value={{
      timezone,
      timezoneAbbr,
      utcOffset,
      updateTimezone,
      toLocalTime,
      formatLocalTime,
      formatGameTime,
      formatGameDateTime,
      timezoneOptions
    }}>
      {children}
    </TimezoneContext.Provider>
  );
};

export default TimezoneProvider;
